import { AimOutlined, CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { IDetectorConfig, ISegmentationConfig, ITrackerConfig, IVisionInferenceModel } from "@kuika/kuika-cl-model";
import { memo, useEffect, useMemo, useState } from "react";
import { Handle, Position, useReactFlow, useStore } from "reactflow";
import { DatasourceService } from "../../../../../../services/datasource-service";
import KuikaBadge from "../../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaDropdownMenu from "../../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { useHistory } from "../history-context";
import ExtendingConnector from "./ExtendingConnector";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import { useNodeCreation } from "./useNodeCreation";
import baseStyles from "./BaseNode.module.css";

/**
 * Combined config type for all processor node types (Detector, Tracker, Segmentation)
 */
type ProcessorConfig = IDetectorConfig &
  ITrackerConfig &
  ISegmentationConfig & {
    status?: "idle" | "loading" | "active";
  };

interface ProcessorNodeData {
  type: "Detector" | "Tracker" | "Segmentation" | "detector" | "tracker" | "segmentation";
  title: string;
  config?: ProcessorConfig;
}

interface ProcessorNodeProps {
  data: ProcessorNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  processor: {
    primaryColor: "#10b981",
    headerBackground: "rgba(16, 185, 129, 0.1)",
    iconColor: "#10b981",
    handleColor: "#10b981"
  }
};

// Cache for inference models to avoid redundant API calls
let cachedModels: IVisionInferenceModel[] | null = null;

// Individual detection classes (without "All Classes")
const INDIVIDUAL_DETECTION_CLASSES: Record<string, string> = {
  bus: "Bus",
  dog: "Dog",
  cat: "Cat",
  car: "Car",
  truck: "Truck",
  person: "Person",
  bicycle: "Bicycle"
};

// Special key for "All Classes" option
const ALL_CLASSES_KEY = "all";

// The × that drops one detection class: a real button, drawn as the bare glyph it replaced (no
// native chrome, the badge's own type and colour). Inline because the node's only stylesheet is
// the shared BaseNode module; the browser's own focus outline is left on for the keyboard.
const CLASS_REMOVE_BUTTON_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  marginLeft: "4px",
  padding: 0,
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  cursor: "pointer"
};

const ProcessorNode = ({ data, id, selected }: ProcessorNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [inferenceModels, setInferenceModels] = useState<IVisionInferenceModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // Fetch inference models from backend
  useEffect(() => {
    const fetchModels = async () => {
      // Use cached models if available
      if (cachedModels) {
        setInferenceModels(cachedModels);
        return;
      }

      setIsLoadingModels(true);
      try {
        const response = await DatasourceService.GetVisionInferenceModels();
        if (response?.data) {
          cachedModels = response.data;
          setInferenceModels(response.data);
        }
      } catch (error) {
        console.error("Failed to load inference models:", error);
      } finally {
        setIsLoadingModels(false);
      }
    };

    fetchModels();
  }, []);

  // Normalize confidence to integer 0-100: if value is a decimal (0-1), convert to percentage
  const normalizeConfidence = (value: number | undefined): number => {
    if (value === undefined) return 50;
    // If value is between 0 and 1 (exclusive), treat as decimal and convert to percentage
    if (value > 0 && value < 1) return Math.round(value * 100);
    // Otherwise treat as already a percentage (0-100)
    return Math.round(value);
  };

  const initialConfidence = normalizeConfidence((data.config as any)?.confidence);

  // Get default model based on node type
  const getDefaultModel = (): string => {
    const nodeType = data.type?.toLowerCase();
    switch (nodeType) {
      case "detector":
        return "yolov8n"; // Default detection model
      case "tracker":
        return "bytetrack"; // Default tracking model
      case "segmentation":
        return "sam2-base"; // Default segmentation model
      default:
        return "";
    }
  };

  const [config, setConfig] = useState(() => {
    // Default config values based on node type
    const defaults = {
      model: getDefaultModel(),
      confidence: 50,
      status: "idle" as "idle" | "loading" | "active",
      classes: [] as string[],
      customClasses: "",
      maxAge: 30,
      fillMask: false,
      device: "cpu" as "cpu" | "cuda"
    };

    // Merge with provided config from template
    if (data.config) {
      return {
        ...defaults,
        ...data.config,
        // Ensure confidence is normalized
        confidence: initialConfidence
      };
    }
    return defaults;
  });
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const { createConnectedNode } = useNodeCreation(id);
  const takeSnapshot = useHistory();

  // Check if source handle is connected - using useStore for reactivity
  const isSourceConnected = useStore(useMemo(() => (state) => state.edges.some((edge) => edge.source === id), [id]));

  const categoryStyles = NODE_CATEGORY_STYLES.processor;

  const updateConfig = (updates: Partial<typeof config>) => {
    // Take snapshot before each config change for undo support
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }

    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Take snapshot before deleting
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  // Sync config state when node data changes from outside (e.g., undo/redo)
  useEffect(() => {
    if (data.config) {
      setConfig((prevConfig) => {
        const stringifiedPrev = JSON.stringify(prevConfig);
        const stringifiedNew = JSON.stringify(data.config);
        // Only update if actually different to avoid unnecessary re-renders
        return stringifiedPrev !== stringifiedNew ? { ...prevConfig, ...data.config } : prevConfig;
      });
    }
  }, [data.config]);

  // Filter models by type from the loaded inference models
  const getModelOptions = (): IVisionInferenceModel[] => {
    const typeMapping: Record<string, string> = {
      Detector: "ObjectDetection",
      detector: "ObjectDetection",
      Tracker: "Tracking",
      tracker: "Tracking",
      Segmentation: "Segmentation",
      segmentation: "Segmentation"
    };
    const modelType = typeMapping[data.type];
    return inferenceModels.filter((model) => model.type === modelType);
  };

  // Normalize node type for UI display (handle both camelCase and PascalCase)
  const getNormalizedType = (): "Detector" | "Tracker" | "Segmentation" => {
    const typeMap: Record<string, "Detector" | "Tracker" | "Segmentation"> = {
      detector: "Detector",
      Detector: "Detector",
      tracker: "Tracker",
      Tracker: "Tracker",
      segmentation: "Segmentation",
      Segmentation: "Segmentation"
    };
    return typeMap[data.type] || "Detector";
  };

  const normalizedType = getNormalizedType();

  const handleNodeSelect = (selectedNodeType: string) => {
    createConnectedNode(selectedNodeType);
  };

  const shouldShowConnector = !isSourceConnected || selected;
  const isCompactMode = isSourceConnected && selected;

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={
          <div className={baseStyles.nodeTooltipContent}>
            {NODE_TOOLTIPS[data.type?.toLowerCase() || "detector"].text}
          </div>
        }
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const renderSummary = () => {
    // If models are still loading, show loading state
    if (isLoadingModels) {
      return (
        <div className={baseStyles.nodeContent}>
          <span className={baseStyles.collapsedSummary}>Loading models...</span>
        </div>
      );
    }

    // Find the model name from the loaded models
    const modelInfo = inferenceModels.find((m) => m.id === config.model);

    // If model not found in available models, show "Select model"
    const availableModels = getModelOptions();
    const isModelValid = availableModels.some((m) => m.id === config.model);
    const modelDisplayName = isModelValid && modelInfo ? modelInfo.name : config.model || "Select model";

    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>
          {modelDisplayName} • {config.confidence ?? 50}%
        </span>
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <AimOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={
              <div className={baseStyles.nodeTooltipContent}>
                {NODE_TOOLTIPS[data.type?.toLowerCase() || "detector"].text}
              </div>
            }
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Model</label>
              <div className={baseStyles.nodrag}>
                <KuikaDropdownMenu
                  options={
                    isLoadingModels
                      ? [{ key: "loading", label: "Loading models...", disabled: true }]
                      : getModelOptions().length === 0
                        ? [{ key: "empty", label: "No models available", disabled: true }]
                        : getModelOptions().map((model) => ({
                            key: model.id,
                            label: model.name
                          }))
                  }
                  selected={config.model}
                  setSelected={(key) => {
                    const selectedModel = inferenceModels.find((m) => m.id === key);
                    const newConfidence = selectedModel?.defaultConfidence ?? 50;
                    updateConfig({ model: key as string, confidence: newConfidence });
                  }}
                  size="md"
                >
                  <div
                    className={baseStyles.formSelect}
                    style={{ cursor: isLoadingModels ? "not-allowed" : "pointer" }}
                  >
                    {isLoadingModels
                      ? "Loading models..."
                      : getModelOptions().find((m) => m.id === config.model)?.name || "Select model"}
                  </div>
                </KuikaDropdownMenu>
              </div>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Device</label>
              <div className={baseStyles.toggleContainer}>
                <span className={config.device === "cpu" ? baseStyles.toggleLabelActive : baseStyles.toggleLabel}>
                  CPU
                </span>
                <button
                  type="button"
                  className={`${baseStyles.toggleSwitch} ${
                    config.device === "cuda" ? baseStyles.toggleSwitchActive : ""
                  } ${baseStyles.nodrag}`}
                  onClick={() => updateConfig({ device: config.device === "cpu" ? "cuda" : "cpu" })}
                  title={config.device === "cpu" ? "Switch to GPU (CUDA)" : "Switch to CPU"}
                >
                  <span className={baseStyles.toggleKnob} />
                </button>
                <span className={config.device === "cuda" ? baseStyles.toggleLabelActive : baseStyles.toggleLabel}>
                  GPU(cuda)
                </span>
              </div>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>
                Confidence (%)
                {(() => {
                  const selectedModel = inferenceModels.find((m) => m.id === config.model);
                  return selectedModel?.defaultConfidence ? (
                    <span
                      className={baseStyles.recommendedHint}
                      title="Click to use recommended value"
                      onClick={() => updateConfig({ confidence: selectedModel.defaultConfidence })}
                    >
                      recommended: {selectedModel.defaultConfidence}%
                    </span>
                  ) : null;
                })()}
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.confidence ?? 50}
                onChange={(e) => updateConfig({ confidence: parseInt(e.target.value) || 0 })}
                className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
              />
            </div>

            {normalizedType === "Detector" && (
              <div className={baseStyles.formGroup}>
                <label className={baseStyles.formLabel}>Detection Classes</label>
                <div className={`${baseStyles.multiSelectContainer} ${baseStyles.nodrag}`}>
                  {/* Selected classes as badges */}
                  <div className={baseStyles.selectedClassesTags}>
                    {(() => {
                      const rawClasses = config.classes || [];
                      const selectedClasses: string[] = Array.isArray(rawClasses) ? rawClasses : [rawClasses];
                      const isAllSelected = selectedClasses.length === 0 || selectedClasses.includes(ALL_CLASSES_KEY);

                      if (isAllSelected) {
                        return <KuikaBadge content="All Classes" color="blue" />;
                      }

                      return selectedClasses.map((classKey) => {
                        const classLabel: string = INDIVIDUAL_DETECTION_CLASSES[classKey] || classKey;
                        return (
                          <KuikaBadge
                            key={classKey}
                            content={classLabel}
                            color="blue"
                            rightIcon={
                              <button
                                type="button"
                                aria-label={`Remove ${classLabel}`}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                  e.stopPropagation();
                                  const newClasses = selectedClasses.filter((c) => c !== classKey);
                                  updateConfig({ classes: newClasses });
                                }}
                                style={CLASS_REMOVE_BUTTON_STYLE}
                              >
                                ×
                              </button>
                            }
                          />
                        );
                      });
                    })()}
                  </div>
                  {/* Dropdown to add classes */}
                  <KuikaDropdownMenu
                    options={[
                      { key: ALL_CLASSES_KEY, label: "All Classes" },
                      ...Object.entries(INDIVIDUAL_DETECTION_CLASSES)
                        .filter(([key]) => {
                          const rawClasses = config.classes || [];
                          const selectedClasses: string[] = Array.isArray(rawClasses) ? rawClasses : [rawClasses];
                          return !selectedClasses.includes(key) && !selectedClasses.includes(ALL_CLASSES_KEY);
                        })
                        .map(([key, label]) => ({ key, label }))
                    ]}
                    selected=""
                    setSelected={(key) => {
                      if (!key) return;
                      const keyStr = String(key);
                      const rawClasses = config.classes || [];
                      const selectedClasses: string[] = Array.isArray(rawClasses) ? rawClasses : [rawClasses];

                      if (keyStr === ALL_CLASSES_KEY) {
                        // "All Classes" selected - clear individual selections (empty array)
                        updateConfig({ classes: [] });
                      } else {
                        // Individual class selected - add to list (remove "all" if present)
                        const newClasses = selectedClasses.filter((c) => c !== ALL_CLASSES_KEY);
                        if (!newClasses.includes(keyStr)) {
                          updateConfig({ classes: [...newClasses, keyStr] });
                        }
                      }
                    }}
                    size="md"
                  >
                    <div className={baseStyles.formSelect} style={{ cursor: "pointer" }}>
                      Add class…
                    </div>
                  </KuikaDropdownMenu>
                </div>
              </div>
            )}

            {normalizedType === "Tracker" && (
              <div className={baseStyles.formGroup}>
                <label className={baseStyles.formLabel}>Max Age (frames)</label>
                <input
                  type="number"
                  className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
                  value={config.maxAge}
                  onChange={(e) => updateConfig({ maxAge: parseInt(e.target.value) })}
                  min="1"
                />
              </div>
            )}

            {/* {normalizedType === "Segmentation" && (
              <div className={baseStyles.formGroup}>
                <label className={baseStyles.formLabel}>
                  <input
                    type="checkbox"
                    checked={config.fillMask || false}
                    onChange={(e) => updateConfig({ fillMask: e.target.checked })}
                    className={baseStyles.nodrag}
                  />{" "}
                  Fill Mask
                </label>
              </div>
            )} */}

            {/* <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => updateConfig({ status: config.status === "active" ? "idle" : "active" })}
            >
              {config.status === "active" ? "Stop" : "Start"} Processing
            </button> */}
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />

      {/* Show extending connector if source handle is not connected OR node is selected */}
      {shouldShowConnector && (
        <ExtendingConnector
          nodeId={id}
          nodeType={data.type || "detector"}
          onNodeSelect={handleNodeSelect}
          compact={isCompactMode}
        />
      )}
    </div>
  );
};

export default memo(ProcessorNode);
